import { memo, useMemo } from 'react'
import CanvasJSReact from '@canvasjs/react-charts'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark, faCheck, faPlus } from '@fortawesome/free-solid-svg-icons'

import { CHART_DEFAULT_OPTIONS } from '../../common/constants'
import { createAxisLabelFormatter } from '../../utils/chartHelpers'
import { YRange } from '../../utils/compareView'
import { cn } from '../../shared/utils/cn'
import { CompareSeries, buildChartConfig } from './compareSeries'

const { CanvasJSChart } = CanvasJSReact

interface Props {
  index: number
  // Series living in this chart
  members: CompareSeries[]
  // Every series in selection order, with the chart it lives in, for the menu
  all: { series: CompareSeries; chartIndex: number }[]
  chartTitle: string
  chartType: string
  isMixed: boolean
  yRange: YRange | null
  axisMin: number
  axisMax: number
  height: number
  onSplitOut: (id: string) => void
  onMoveInto: (id: string, chartIndex: number) => void
  // Absent when this is the only chart
  onRemove?: (chartIndex: number) => void
  register: (id: string) => (chart: any) => void
  rangeChanged: (e: any) => void
}

/**
 * One chart of the compare stack. The chips are the series in this chart
 * (click one to move it out to a chart of its own); the "Sensors" menu lists
 * every series, ticked when it is on this chart, so others can be brought in.
 * Nothing is ever hidden; to drop a series, untick it in the grid above.
 */
export const CompareGroupChart = memo(function CompareGroupChart({
  index,
  members,
  all,
  chartTitle,
  chartType,
  isMixed,
  yRange,
  axisMin,
  axisMax,
  height,
  onSplitOut,
  onMoveInto,
  onRemove,
  register,
  rangeChanged,
}: Props) {
  const labelFormatter = useMemo(() => createAxisLabelFormatter(), [])

  const config = useMemo(
    () => buildChartConfig(members, { chartType, chartTitle, isMixed, yRange }),
    [members, chartType, chartTitle, isMixed, yRange]
  )

  const chartKey = members.map(m => m.id).join('|')

  const options = useMemo(
    () => ({
      ...CHART_DEFAULT_OPTIONS,
      height,
      zoomEnabled: true,
      exportFileName: `Compare_${members.length === 1 ? members[0].label : chartTitle}`,
      title: { text: '' },
      rangeChanged,
      // useSyncedCharts draws the tooltip at the cursor's exact time on every
      // chart; the built-in one would snap to this chart's nearest point
      toolTip: { enabled: false },
      axisY: config.axisY,
      ...(config.axisY2 ? { axisY2: config.axisY2 } : {}),
      axisX: {
        labelFormatter,
        viewportMinimum: axisMin,
        viewportMaximum: axisMax,
        crosshair: { enabled: true, snapToDataPoint: false, labelFormatter: () => '' },
      },
      data: config.data,
    }),
    [config, height, members, chartTitle, axisMin, axisMax, labelFormatter, rangeChanged]
  )

  const lone = members.length === 1 ? members[0].data : null
  const heading = lone
    ? `${isMixed ? lone.sensorTypeLabel : chartTitle} · ${lone.deviceName} ${lone.sensorPort}`
    : `${isMixed ? 'Mixed sensors' : chartTitle} · ${members.length} sensors`
  const canSplit = members.length > 1
  const allEmpty = members.every(m => !m.data.hasData)

  const sensorsMenu =
    all.length > 1 ? (
      <SensorsMenu
        all={all}
        chartIndex={index}
        memberCount={members.length}
        onPick={(id, here) => (here ? onSplitOut(id) : onMoveInto(id, index))}
      />
    ) : null

  return (
    <section className="border-b border-gray-100 last:border-b-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 pt-3">
        <h3 className="flex flex-wrap items-center gap-x-2 text-sm font-semibold text-gray-800">
          {members.length === 1 && (
            <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: members[0].color }} aria-hidden="true" />
          )}
          {heading}
          {config.caption && <span className="text-xs font-normal text-gray-500">{config.caption}</span>}
          {allEmpty && <span className="text-xs font-normal text-gray-500">· No data for this period</span>}
        </h3>
        {members.length === 1 && sensorsMenu}
        {onRemove && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            aria-label={`Remove this chart; its sensors move to chart ${index === 0 ? 2 : index}`}
            title={`Remove chart (its sensors move to the chart ${index === 0 ? 'below' : 'above'})`}
            className="ml-auto px-2 py-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        )}
      </div>

      {/* A lone series is already named by the heading, so it gets no chip */}
      {members.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 px-4 py-2">
          {members.map(m => (
            <button
              key={m.id}
              type="button"
              disabled={!canSplit}
              onClick={() => onSplitOut(m.id)}
              aria-label={canSplit ? `Remove ${m.label} from this chart; it moves to its own chart` : m.label}
              title={canSplit ? 'Remove from this chart (moves to its own chart)' : undefined}
              className={cn(
                'inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium bg-gray-100',
                canSplit ? 'hover:bg-gray-200' : 'cursor-default'
              )}
            >
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: m.color }} />
              <span>{m.label}</span>
              {!m.data.hasData && <span className="text-gray-400 text-[10px]">(No data)</span>}
              {canSplit && <FontAwesomeIcon icon={faXmark} className="text-gray-400 text-[10px]" />}
            </button>
          ))}

          {sensorsMenu}
        </div>
      )}

      <CanvasJSChart key={chartKey} options={options} onRef={register(chartKey)} />
    </section>
  )
})

interface SensorsMenuProps {
  all: { series: CompareSeries; chartIndex: number }[]
  // The chart this menu belongs to, or null for the blank "Add chart" placeholder
  chartIndex: number | null
  memberCount: number
  // here: the series is already on this chart (so picking it moves it out)
  onPick: (id: string, here: boolean) => void
  label?: string
}

// Every series, ticked when it is on this chart and labelled with its chart otherwise
export function SensorsMenu({ all, chartIndex, memberCount, onPick, label = 'Sensors' }: SensorsMenuProps) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded border border-gray-300 text-xs text-gray-600 hover:bg-gray-50 data-[state=open]:bg-gray-100"
        >
          <FontAwesomeIcon icon={faPlus} className="text-[10px]" />
          {label}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          className="z-50 min-w-[18rem] max-h-80 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          <DropdownMenu.Label className="px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">
            On this chart
          </DropdownMenu.Label>
          {all.map(({ series: s, chartIndex: on }) => {
            const here = on === chartIndex
            // The last series on a chart can't leave it (it is already alone)
            const locked = here && memberCount === 1
            return (
              <DropdownMenu.CheckboxItem
                key={s.id}
                checked={here}
                disabled={locked}
                onSelect={() => onPick(s.id, here)}
                className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs text-gray-700 outline-none data-[highlighted]:bg-gray-50 data-[disabled]:cursor-default data-[disabled]:text-gray-400"
              >
                <span className="flex w-3 justify-center">
                  <DropdownMenu.ItemIndicator>
                    <FontAwesomeIcon icon={faCheck} className="text-[10px] text-indigo-600" />
                  </DropdownMenu.ItemIndicator>
                </span>
                <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: s.color }} />
                <span className="flex-1">{s.label}</span>
                <span className="text-[10px] text-gray-400">
                  {here ? (locked ? 'only sensor' : 'move out') : `chart ${on + 1}`}
                </span>
              </DropdownMenu.CheckboxItem>
            )
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

